import { atom } from 'nanostores';
import { db, chatId } from '~/lib/persistence/useChatHistory';
import { updateProjectRules, getRules } from '~/lib/persistence/db';
import { selectionStore } from '~/lib/persistence/selectionStore';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

interface RuleState {
  projectRules: string | null; // Content of the rules file
  loaded: boolean; // Whether rules have been loaded from the database
  source: 'chat' | 'temporary' | null; // Track where rules came from
}

export const rulesStore = atom<RuleState>({
  projectRules: null,
  loaded: false,
  source: null,
});

export async function setProjectRules(rules: string): Promise<boolean> {
  const currentChatId = chatId.get();
  const isTemporary = !currentChatId;

  // Update store state immediately for responsive UI
  rulesStore.set({
    projectRules: rules,
    loaded: true,
    source: isTemporary ? 'temporary' : 'chat',
  });

  // Store in localStorage for temporary rules, or IndexedDB for persistent rules
  if (isTemporary) {
    console.log('[rules] Saving temporary rules to localStorage');
    selectionStore.setKey('temporaryProjectRules', rules);
    return true;
  }

  // Store in IndexedDB if we have a database and chat ID
  if (db && currentChatId) {
    try {
      console.log('[rules] Saving rules to IndexedDB for chat:', currentChatId);

      const success = await updateProjectRules(db, currentChatId, rules);

      if (success) {
        console.log('[rules] Successfully saved rules to IndexedDB');

        // Clear any temporary rules if successful
        if (selectionStore.get().temporaryProjectRules) {
          selectionStore.setKey('temporaryProjectRules', '');
        }

        return true;
      }

      console.warn('[rules] Failed to save rules to IndexedDB');
      return false;
    } catch (error) {
      /*
       * Sentry.captureException(error, {
       *   extra: { message: 'Error saving rules to IndexedDB', chatId: currentChatId, rules },
       * });
       */
      console.error('[rules] Error saving rules to IndexedDB:', error);
      return false;
    }
  }

  return false;
}

export async function loadChatSpecificRules(): Promise<void> {
  // Set initial loading state
  rulesStore.set({
    projectRules: null,
    loaded: false,
    source: null,
  });

  const currentChatId = chatId.get();
  const tempRules = selectionStore.get().temporaryProjectRules;

  console.log('[rules] Loading rules - chatId:', currentChatId, 'tempRules exist:', !!tempRules);

  try {
    // Handle no database case first (simplifies logic)
    if (!db) {
      console.log('[rules] No database available');

      // If we have temporary rules and no chatId, use them
      if (tempRules && !currentChatId) {
        rulesStore.set({
          projectRules: tempRules,
          loaded: true,
          source: 'temporary',
        });
        return;
      }

      // Otherwise, empty rules
      rulesStore.set({
        projectRules: null,
        loaded: true,
        source: null,
      });
      return;
    }

    // CASE: No chat ID - use temporary rules or empty
    if (!currentChatId) {
      rulesStore.set({
        projectRules: tempRules || null,
        loaded: true,
        source: tempRules ? 'temporary' : null,
      });

      console.log(
        tempRules
          ? '[rules] Using temporary rules from localStorage'
          : '[rules] No chat ID and no temporary rules, initializing empty',
      );

      return;
    }

    // CASE: Chat ID exists

    // If we have temporary rules, migrate them
    if (tempRules) {
      console.log('[rules] Migrating temporary rules to chat:', currentChatId);

      const success = await updateProjectRules(db, currentChatId, tempRules);

      if (success) {
        console.log('[rules] Successfully migrated temporary rules');
        selectionStore.setKey('temporaryProjectRules', '');

        rulesStore.set({
          projectRules: tempRules,
          loaded: true,
          source: 'chat',
        });
        return;
      }

      console.log('[rules] Migration failed, loading existing rules');
    }

    // Load existing rules from database
    console.log('[rules] Loading existing rules for chat:', currentChatId);

    const rules = await getRules(db, currentChatId);

    rulesStore.set({
      projectRules: rules,
      loaded: true,
      source: rules ? 'chat' : null,
    });

    console.log(rules ? '[rules] Loaded rules successfully' : '[rules] No rules found for this chat');
  } catch (error) {
    /*
     * Sentry.captureException(error, {
     *   extra: { message: 'Error loading rules', chatId: currentChatId },
     *   level: 'log',
     * });
     */
    console.error('[rules] Error loading rules:', error);

    rulesStore.set({
      projectRules: null,
      loaded: true,
      source: null,
    });
  }
}

export function clearProjectRules(): void {
  console.log('[rules] Clearing all project rules');

  rulesStore.set({
    projectRules: null,
    loaded: true,
    source: null,
  });

  if (selectionStore.get().temporaryProjectRules) {
    selectionStore.setKey('temporaryProjectRules', '');
  }
}
